/**
 * The visible people for a reduced People Chart: rule matches plus the
 * primary reporting ancestry needed to keep those matches intelligible.
 *
 * This operates only on an already permissioned model. It does not evaluate
 * a saved-view rule or inspect canonical data.
 */
export interface PeopleChartReducedMembership {
  readonly matchingPersonIds: ReadonlySet<string>;
  readonly includedPersonIds: ReadonlySet<string>;
  readonly reportingChainContextPersonIds: ReadonlySet<string>;
  readonly cycleDetected: boolean;
}

export function buildPeopleChartReducedMembership(input: {
  readonly personIds: Iterable<string>;
  readonly matchingPersonIds: Iterable<string>;
  readonly managerByPersonId: ReadonlyMap<string, string>;
}): PeopleChartReducedMembership {
  const visiblePersonIds = new Set(input.personIds);
  const matchingPersonIds = new Set(
    Array.from(input.matchingPersonIds).filter((personId) => visiblePersonIds.has(personId)),
  );
  const includedPersonIds = new Set(matchingPersonIds);
  let cycleDetected = false;

  for (const matchingPersonId of matchingPersonIds) {
    const visited = new Set<string>([matchingPersonId]);
    let managerId = input.managerByPersonId.get(matchingPersonId);
    while (managerId) {
      if (!visiblePersonIds.has(managerId)) break;
      if (visited.has(managerId)) {
        cycleDetected = true;
        break;
      }
      visited.add(managerId);
      includedPersonIds.add(managerId);
      managerId = input.managerByPersonId.get(managerId);
    }
  }

  const reportingChainContextPersonIds = new Set(
    Array.from(includedPersonIds).filter((personId) => !matchingPersonIds.has(personId)),
  );
  return {
    matchingPersonIds,
    includedPersonIds,
    reportingChainContextPersonIds,
    cycleDetected,
  };
}
